import * as z from "zod";

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/iu;

export function isUuid(value: string): boolean {
  return UUID_PATTERN.test(value);
}

/**
 * The thread id in a bare id or either D-23 permalink form
 * (`…/comments/<id>`, `…/<folderId>?thread=<id>`); null when there is none.
 */
export function parseCommentThreadReference(reference: string): string | null {
  const trimmed = reference.trim();
  if (isUuid(trimmed)) {
    return trimmed;
  }
  const url = URL.parse(trimmed);
  if (!url) {
    return null;
  }
  const threadParam = url.searchParams.get("thread");
  if (threadParam && isUuid(threadParam)) {
    return threadParam;
  }
  const pathMatch = /\/comments\/([^/]+)\/?$/u.exec(url.pathname)?.[1];
  return pathMatch && isUuid(pathMatch) ? pathMatch : null;
}

/** A comment body an agent or API caller writes: something besides whitespace. */
export const commentBodyTextSchema = z
  .string()
  .min(1)
  .refine((body) => body.trim().length > 0, {
    message: "Write something before posting.",
  });

export const commentSourceSchema = z
  .enum(["ui", "agent", "mcp", "api"])
  .describe("CommentSource");

const unitIntervalSchema = z.number().min(0).max(1);

// Anchors come from the commenter's client and are returned with every thread
// list, including anonymous artifact loads, so each field is bounded. The
// limits sit well above what capture produces (deep or shadow-root paths).
const anchorUrlSchema = z.string().max(2048);
const anchorLocaleSchema = z.string().max(35);
const anchorSelectorSchema = z.string().max(4000);
const anchorIdSchema = z.string().max(256);

export const commentAnchorContextSchema = z
  .object({
    url: anchorUrlSchema,
    locale: anchorLocaleSchema.nullish(),
  })
  .describe("CommentAnchorContext");

export const commentAnchorNoneSchema = z
  .object({
    type: z.literal("none"),
    context: commentAnchorContextSchema.optional(),
  })
  .describe("CommentAnchorNone");

export const commentAnchorElementSchema = z
  .object({
    type: z.literal("element"),
    semanticSelector: anchorSelectorSchema.optional(),
    selector: anchorSelectorSchema,
    nthOfTypePath: anchorSelectorSchema,
    source: z
      .object({
        filePath: z.string().max(1024),
        componentChain: z.array(anchorIdSchema).max(50),
        line: z.number().int().optional(),
        col: z.number().int().optional(),
      })
      .optional(),
    listKey: anchorIdSchema.optional(),
    tagName: z.string().max(64),
    textPreview: z.string().max(200),
    quote: z
      .object({
        exact: z.string().max(2000),
        prefix: z.string().max(80),
        suffix: z.string().max(80),
      })
      .optional(),
    offset: z.object({ x: unitIntervalSchema, y: unitIntervalSchema }),
    context: commentAnchorContextSchema.optional(),
  })
  .describe("CommentAnchorElement");

export const commentAnchorCanvasSchema = z
  .object({
    type: z.literal("canvas"),
    nodeId: anchorIdSchema,
    layerId: anchorIdSchema.optional(),
    point: z.object({ x: z.number(), y: z.number() }),
    world: z
      .object({ x: z.number(), y: z.number() })
      .optional()
      .describe("Set only for free-floating pins that sit on no node."),
  })
  .describe("CommentAnchorCanvas");

export const commentAnchorPointSchema = z
  .object({
    type: z.literal("point"),
    page: z.number().int().min(0).optional(),
    x: unitIntervalSchema,
    y: unitIntervalSchema,
  })
  .describe("CommentAnchorPoint");

export const commentAnchorSchema = z
  .discriminatedUnion("type", [
    commentAnchorNoneSchema,
    commentAnchorElementSchema,
    commentAnchorCanvasSchema,
    commentAnchorPointSchema,
  ])
  .describe("CommentAnchor");

export const commentTargetSchema = z
  .discriminatedUnion("kind", [
    z.object({
      kind: z.literal("file"),
      fileId: z.uuid(),
      folderId: z
        .uuid()
        .optional()
        .describe(
          "The artifact folder the comment was left from; the file must be one of its pages.",
        ),
    }),
    z.object({
      kind: z.literal("sitePage"),
      siteId: z.uuid(),
      url: anchorUrlSchema,
      locale: anchorLocaleSchema.nullish(),
      routePatternHint: anchorUrlSchema.optional(),
      via: z
        .object({ fileId: z.uuid(), shareId: z.uuid() })
        .optional()
        .describe(
          "Set when the site preview is embedded in an artifact or canvas.",
        ),
    }),
    z.object({ kind: z.literal("order"), orderId: z.uuid() }),
    z.object({ kind: z.literal("asset"), assetId: z.uuid() }),
  ])
  .describe("CommentTarget");

export const commentSurfaceSchema = z
  .enum([
    "artifact",
    "canvas",
    "image",
    "markdown",
    "pdf",
    "file",
    "websiteBuilder",
    "embeddedPreview",
    "orders",
    "assets",
  ])
  .describe("CommentSurface");

export const commentAuthorSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    avatarUrl: z.string().nullable(),
    deleted: z.boolean(),
  })
  .describe("CommentAuthor");

export const commentSchema = z
  .object({
    id: z.string(),
    threadId: z.string(),
    authorUserId: z.string(),
    source: commentSourceSchema,
    body: z.string().describe("Empty when the comment is deleted."),
    createdAt: z.iso.datetime(),
    editedAt: z.iso.datetime().nullable(),
    deletedAt: z.iso.datetime().nullable(),
  })
  .describe("Comment");

export const commentReactionSchema = z
  .object({
    commentId: z.string(),
    userId: z.string(),
    emoji: z.string(),
    createdAt: z.iso.datetime(),
  })
  .describe("CommentReaction");

export const commentThreadTargetSchema = z
  .object({
    fileId: z.string().optional(),
    siteId: z.string().optional(),
    path: z.string().optional(),
    orderId: z.string().optional(),
    assetId: z.string().optional(),
  })
  .describe("CommentThreadTarget");

export const commentThreadSchema = z
  .object({
    id: z.string(),
    projectId: z.string(),
    target: commentThreadTargetSchema,
    viaFileId: z.string().nullable(),
    viaFileName: z.string().nullable(),
    anchor: commentAnchorSchema,
    resolvedAt: z.iso.datetime().nullable(),
    resolvedByUserId: z.string().nullable(),
    resolvedSource: commentSourceSchema.nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
    deletedAt: z.iso.datetime().nullable(),
  })
  .describe("CommentThread");

export const commentViewerSchema = z
  .object({
    userId: z
      .string()
      .nullable()
      .describe("The signed-in caller; null for anonymous viewers."),
    isMember: z
      .boolean()
      .describe("Holds a role on the project, so object mentions are on."),
    canComment: z.boolean(),
    canModerate: z
      .boolean()
      .describe("A workspace admin, who can delete anyone's comment (D-10)."),
  })
  .describe("CommentViewer");

export const commentThreadBundleSchema = z
  .object({
    threads: z.array(commentThreadSchema),
    comments: z.array(commentSchema),
    reactions: z.array(commentReactionSchema),
    authors: z.array(commentAuthorSchema),
    viewer: commentViewerSchema,
  })
  .describe("CommentThreadBundle");

export const createCommentThreadBodySchema = z
  .object({
    id: z.uuid(),
    target: commentTargetSchema,
    anchor: commentAnchorSchema,
    body: z.object({ id: z.uuid(), body: z.string() }),
  })
  .describe("CreateCommentThreadBody");

export const updateCommentThreadBodySchema = z
  .object({
    resolved: z.boolean().optional(),
    path: z
      .string()
      .optional()
      .describe("Moves the thread to another page and resets its anchor."),
    anchor: commentAnchorSchema.optional(),
  })
  .refine(
    (patch) => {
      return (
        patch.resolved !== undefined ||
        patch.path !== undefined ||
        patch.anchor !== undefined
      );
    },
    { message: "Pass at least one of resolved, path or anchor." },
  )
  .refine((patch) => patch.path === undefined || patch.anchor === undefined, {
    message: "Move a thread or re-anchor it, not both at once.",
  })
  .describe("UpdateCommentThreadBody");

export const createCommentBodySchema = z
  .object({
    id: z.uuid(),
    body: z.string(),
  })
  .describe("CreateCommentBody");

export const updateCommentBodySchema = z
  .object({
    body: z.string(),
  })
  .describe("UpdateCommentBody");

export const commentThreadStatusSchema = z
  .enum(["open", "resolved"])
  .describe("CommentThreadStatus");

export const listCommentThreadsQuerySchema = z
  .object({
    fileId: z.uuid().optional(),
    folderId: z
      .uuid()
      .optional()
      .describe("Artifact folder: every page under it."),
    siteId: z.uuid().optional(),
    path: z
      .string()
      .optional()
      .describe(
        "A concrete page path or a route pattern, normalized the same way a new comment's URL is.",
      ),
    locale: z
      .string()
      .optional()
      .describe("The locale prefixed to `path`, which is stripped first."),
    viaFileId: z.uuid().optional(),
    orderId: z.uuid().optional(),
    assetId: z.uuid().optional(),
    status: commentThreadStatusSchema.optional(),
  })
  .refine(
    (query) => {
      return (
        [
          query.fileId,
          query.folderId,
          query.siteId,
          query.orderId,
          query.assetId,
        ].filter((value) => value !== undefined).length === 1
      );
    },
    {
      message:
        "Exactly one of fileId, folderId, siteId, orderId or assetId is required.",
    },
  )
  .refine(
    (query) => {
      return (
        query.siteId !== undefined ||
        (query.path === undefined &&
          query.locale === undefined &&
          query.viaFileId === undefined)
      );
    },
    { message: "path, locale and viaFileId require siteId." },
  )
  .describe("ListCommentThreadsQuery");

export const commentThreadLocationSchema = z
  .object({
    kind: z.enum(["artifact", "miniapp", "commentsScreen", "gone"]),
    url: z.string().optional(),
    projectId: z
      .string()
      .optional()
      .describe(
        "The thread's project, set for miniapp and commentsScreen locations so the dashboard can build the project route.",
      ),
    miniappId: z.string().optional(),
    miniappRoute: z.string().optional(),
  })
  .describe("CommentThreadLocation");

export const presignCommentAttachmentBodySchema = z
  .object({
    target: commentTargetSchema,
    contentType: z.string(),
    contentLength: z.number().int().positive(),
  })
  .describe("PresignCommentAttachmentBody");

export const presignCommentAttachmentResponseSchema = z
  .object({
    objectKey: z.string(),
    presignedPutUrl: z.string(),
    publicUrl: z.string(),
  })
  .describe("PresignCommentAttachmentResponse");

export const commitCommentAttachmentBodySchema = z
  .object({
    target: commentTargetSchema,
    objectKey: z.string(),
  })
  .describe("CommitCommentAttachmentBody");

export const commitCommentAttachmentResponseSchema = z
  .object({
    publicUrl: z.string(),
  })
  .describe("CommitCommentAttachmentResponse");

export const commentSiteThreadSortSchema = z
  .object({
    column: z.enum([
      "page",
      "excerpt",
      "author",
      "created",
      "lastActivity",
      "replies",
      "status",
      "from",
    ]),
    direction: z.enum(["asc", "desc"]),
  })
  .describe("CommentSiteThreadSort");

export const commentSiteThreadPageSchema = z
  .object({
    threads: z.array(commentThreadSchema),
    comments: z.array(commentSchema),
    reactions: z.array(commentReactionSchema),
    authors: z.array(commentAuthorSchema),
    orphanedPaths: z
      .array(z.string())
      .nullable()
      .describe(
        "Paths on this page with no current site page; null when the site's pages couldn't be read.",
      ),
    nextCursor: z.string().nullable(),
  })
  .describe("CommentSiteThreadPage");

export const commentMentionPersonSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    avatarUrl: z.string().nullable(),
  })
  .describe("CommentMentionPerson");

export const mentionableDisplaySchema = z
  .object({
    label: z.string(),
    imageUrl: z.string().optional(),
  })
  .describe("MentionableDisplay");

export const mentionableResolutionSchema = z
  .discriminatedUnion("state", [
    z.object({ state: z.literal("ok"), display: mentionableDisplaySchema }),
    z.object({
      state: z.literal("forbidden"),
      category: z
        .string()
        .describe(
          'A category label such as "File", never the object\'s title.',
        ),
    }),
  ])
  .describe("MentionableResolution");

export const commentThreadTargetSummarySchema = z
  .object({
    kind: z.enum(["file", "sitePage", "order", "asset"]),
    fileId: z.string().optional(),
    fileName: z.string().optional(),
    filePath: z
      .string()
      .optional()
      .describe("The file's path from its project's root folder."),
    artifactFolderId: z
      .string()
      .optional()
      .describe("Set when the file is a page of an artifact."),
    fileDeleted: z
      .boolean()
      .optional()
      .describe("True when the commented file no longer exists."),
    siteId: z.string().optional(),
    path: z.string().optional().describe("The site route pattern."),
    url: z
      .string()
      .optional()
      .describe("The concrete page URL the comment was left on."),
    orderId: z.string().optional(),
    assetId: z.string().optional(),
  })
  .describe("CommentThreadTargetSummary");

export const commentThreadDetailSchema = z
  .object({
    thread: commentThreadSchema,
    comments: z.array(commentSchema),
    reactions: z.array(commentReactionSchema),
    authors: z.array(commentAuthorSchema),
    target: commentThreadTargetSummarySchema,
  })
  .describe("CommentThreadDetail");

/**
 * The realtime keys `comments-changed` is broadcast under (D-26 §2). The
 * server sends every key a thread belongs to; a client listens on the one its
 * list query covers.
 */
export const commentsChangedKeys = {
  file: (fileId: string) => `file:${fileId}`,
  folder: (folderId: string) => `folder:${folderId}`,
  /** One page; `path` is the stored, canonical route pattern. */
  sitePage: (siteId: string, path: string) => `site:${siteId}:${path}`,
  /** Every page of a site. */
  site: (siteId: string) => `site:${siteId}:*`,
  order: (orderId: string) => `order:${orderId}`,
  asset: (assetId: string) => `asset:${assetId}`,
};
